import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parse } from 'date-fns';
import { es } from 'date-fns/locale';

import Button from '@/components/Button';
import OptionButton from '@/components/Button/otherButtons/OptionButton';
import RangeDatePicker from '@/components/RangeDatePicker/RangeDatePicker';
import { datePickerSlotProps } from '@/utils/datePickerConfig';

import useDatePickerModal from './useDatePickerModal';

export interface DatePickerModalProps {
  handleChangeDate: ({ startDate, endDate }: DateProps) => void;
  title: string;
  onClose: () => void;
}

interface DateProps {
  startDate: string;
  endDate: string;
}

const DatePickerModal = ({ handleChangeDate, title, onClose }: DatePickerModalProps) => {
  const theme = useTheme();
  const hook = useDatePickerModal({ handleChangeDate, onClose });

  return (
    <Box>
      {hook.optionSelected === 0 ? (
        <>
          <Typography variant="subtitle2">{title}</Typography>

          <Box sx={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <OptionButton handleClick={() => hook.handleOptionSelected(1)}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  '& span': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Por <span>día</span>
              </Typography>
            </OptionButton>
            <OptionButton handleClick={() => hook.handleOptionSelected(2)}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  '& span': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Por <span>rango de </span>
                <span>fechas</span>
              </Typography>
            </OptionButton>
          </Box>
        </>
      ) : (
        <>
          {hook.optionSelected === 1 && (
            <Box sx={{ pb: 1 }}>
              <Typography variant="subtitle2" mb={2}>
                Seleccionar fecha
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  sx={{ width: '100%' }}
                  label="Fecha"
                  value={hook.dates.startDate}
                  onChange={hook.handleOneDateChange}
                  slots={{ textField: (params) => <TextField {...params} /> }}
                  slotProps={datePickerSlotProps}
                />
              </LocalizationProvider>
            </Box>
          )}
          {hook.optionSelected === 2 && (
            <Box sx={{ pb: 1 }}>
              <Typography variant="subtitle2" mb={2}>
                Seleccionar rango de fechas
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <RangeDatePicker
                  startLabel="Desde"
                  endLabel="Hasta"
                  noPadding
                  onChange={(e, v) => {
                    const parsedDate = parse(v, 'yyyy-MM-dd', new Date());
                    if (e === 'startDate') {
                      hook.setDates({ ...hook.dates, startDate: parsedDate });
                    }
                    if (e === 'endDate') {
                      hook.setDates({ ...hook.dates, endDate: parsedDate });
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          )}

          <Grid
            size={{ xxs: 12 }}
            container
            sx={{
              display: 'flex',
              justifyContent: { xxs: 'center', xs: 'space-between' },
              marginTop: '1rem',
              gap: '0.5rem',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              paddingTop: '1rem',
              zIndex: 1,
            }}
          >
            <Button variant="outlined" onClick={() => hook.handleOptionSelected(0)}>
              Regresar
            </Button>
            <Button variant="contained" onClick={hook.submitDates}>
              Aceptar
            </Button>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DatePickerModal;
